import React, { useState, useRef, useEffect } from "react";
import { 
    Plus, Film, Search, Trash2, DollarSign, Clock, 
    CheckCircle2, Circle, Sparkles, Layers, AlertTriangle,
    Pencil, Image as ImageIcon, Save, X, UploadCloud
} from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Added for upload handling

// Shared UI Components
import { 
    BlackboxLoaderIcon, 
    AppModal 
} from '../components/SharedUI'; 

const ProjectDashboard = ({
    projects, 
    isLoading, 
    onSelectProject, 
    onDeleteProject,
    onUpdateProject, 
    onCreateNew,
    setNotification,
    user,   // Required for upload path
    storage // Required for upload actions
}) => {
    // --- STATE ---
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    
    // Deletion State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Editing State
    const [editingProject, setEditingProject] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    // New: File Upload State for Editor
    const [newPosterFile, setNewPosterFile] = useState(null);
    const [posterPreview, setPosterPreview] = useState(null);

    // Hover State
    const [hoveredProjectId, setHoveredProjectId] = useState(null);

    // --- LOGIC ---

    const toggleSelection = (id, e) => {
        if (e) e.stopPropagation();
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedIds(newSet);
        
        if (!isSelectionMode && newSet.size > 0) setIsSelectionMode(true);
        if (isSelectionMode && newSet.size === 0) setIsSelectionMode(false);
    };

    const toggleSelectionMode = () => {
        setIsSelectionMode(!isSelectionMode);
        setSelectedIds(new Set());
    };

    const initiateBulkDelete = () => {
        if (selectedIds.size === 0) return;
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        const idsToDelete = Array.from(selectedIds);
        setShowDeleteModal(false); 

        try {
            await Promise.allSettled(idsToDelete.map(id => onDeleteProject(id)));
            if (setNotification) {
                setNotification({ type: 'success', message: `Deleted ${idsToDelete.length} project(s).` });
            }
        } catch (error) {
            console.warn("Bulk delete finished with minor warnings.", error);
        } finally {
            setIsDeleting(false);
            setSelectedIds(new Set());
            setIsSelectionMode(false);
        }
    };

    // --- NEW: Handle Opening Edit Modal ---
    const openEditModal = (project) => {
        setEditingProject(project);
        setPosterPreview(project.poster_a || null); // Initialize with existing poster
        setNewPosterFile(null); // Reset new file
    };

    // --- NEW: Handle File Select in Editor ---
    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setNewPosterFile(file);
            setPosterPreview(URL.createObjectURL(file)); // Show immediate local preview
        }
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        if (!editingProject) return;
        if (!onUpdateProject) return;

        setIsSaving(true);
        try {
            let finalPosterUrl = editingProject.poster_a;

            // 1. Upload new image if selected
            if (newPosterFile && user && storage) {
                const fileName = `poster_${Date.now()}_${newPosterFile.name}`;
                const storageRef = ref(storage, `users/${user.uid}/project_posters/${fileName}`);
                await uploadBytes(storageRef, newPosterFile);
                finalPosterUrl = await getDownloadURL(storageRef);
            }

            // 2. Prepare updated object
            const updatedData = {
                ...editingProject,
                poster_a: finalPosterUrl
            };

            // 3. Save to DB
            await onUpdateProject(updatedData);
            
            setEditingProject(null);
            if (setNotification) setNotification({ type: 'success', message: 'Project updated successfully.' });
        } catch (error) {
            console.error("Update failed", error);
            if (setNotification) setNotification({ type: 'error', message: 'Failed to update project.' });
        } finally {
            setIsSaving(false);
        }
    };

    // Filter & Sort
    const sortedProjects = [...projects].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
    
    const filteredProjects = sortedProjects.filter(project =>
        project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.logline?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.genre?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const showHero = !searchQuery && !isSelectionMode && filteredProjects.length > 0;
    const heroProject = showHero ? filteredProjects[0] : null;
    const gridProjects = showHero ? filteredProjects.slice(1) : filteredProjects;

    const formatDate = (date) => {
        if (!date) return '';
        const d = date.toDate ? date.toDate() : new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // --- RENDER ---

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh]">
                <BlackboxLoaderIcon className="w-16 h-16 mb-6" />
                <p className="text-zinc-500 font-mono animate-pulse uppercase tracking-widest text-xs">Loading Studio Data...</p>
            </div>
        );
    }

    if (projects.length === 0) {
        return <EmptyState onCreateNew={onCreateNew} />;
    }

    return (
        <div className="pb-32 space-y-8 md:space-y-12 relative min-h-screen">
            
            {/* --- 1. HEADER & CONTROLS --- */}
            <header className="sticky top-0 z-30 py-4 -mx-4 px-4 md:-mx-8 md:px-8 bg-zinc-950/90 backdrop-blur-xl border-b border-white/5 flex flex-col md:flex-row gap-4 justify-between items-center transition-all duration-300">
                <div className="relative w-full md:w-96 group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within:text-purple-400 transition-colors w-4 h-4" />
                    <input 
                        type="text" 
                        placeholder="Search studio..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-zinc-900/50 border border-zinc-800 text-zinc-200 pl-10 pr-4 py-2.5 rounded-full focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all placeholder:text-zinc-600 text-sm"
                    />
                </div>

                <div className="flex w-full md:w-auto items-center justify-between md:justify-end gap-3">
                    <button 
                        onClick={toggleSelectionMode}
                        className={`
                            flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-all border
                            ${isSelectionMode 
                                ? 'bg-purple-500/10 border-purple-500/50 text-purple-400' 
                                : 'bg-transparent border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'}
                        `}
                    >
                        <Layers size={14} />
                        <span>{isSelectionMode ? 'Done' : 'Manage'}</span>
                    </button>

                    <button 
                        onClick={onCreateNew}
                        className="flex items-center gap-2 px-5 py-2.5 bg-zinc-100 hover:bg-white text-black text-sm font-bold rounded-full transition-all hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] active:scale-95"
                    >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline">New Project</span>
                    </button>
                </div>
            </header>

            {/* --- 2. HERO SECTION --- */}
            <AnimatePresence>
                {heroProject && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, height: 0 }}
                        className="relative w-full min-h-[500px] h-auto rounded-3xl overflow-hidden group cursor-pointer border border-white/10 shadow-2xl flex flex-col justify-end"
                        onClick={() => onSelectProject(heroProject.id)}
                    >
                        {/* Background */}
                        <div className="absolute inset-0 z-0">
                            {heroProject.poster_a ? (
                                <img src={heroProject.poster_a} alt="Hero" className="w-full h-full object-cover blur-sm scale-105 opacity-60 group-hover:scale-110 transition-transform duration-1000" />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-purple-900/20 to-zinc-950" />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black via-black/80 to-transparent" />
                        </div>

                        {/* Content */}
                        <div className="relative z-10 p-6 md:p-12 w-full max-w-4xl">
                            <div className="flex items-center justify-between w-full mb-6">
                                <div className="flex items-center gap-3">
                                    <span className="px-3 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 text-[10px] font-bold uppercase tracking-widest rounded-full backdrop-blur-md">
                                        Active Production
                                    </span>
                                    <span className="text-zinc-400 text-xs flex items-center gap-1">
                                        <Clock size={12} /> {formatDate(heroProject.updated_at)}
                                    </span>
                                </div>
                                
                                <button 
                                    onClick={(e) => { e.stopPropagation(); openEditModal(heroProject); }}
                                    className="p-3 rounded-full bg-white/10 text-white hover:bg-white hover:text-black backdrop-blur-md transition-all shadow-lg border border-white/10"
                                    title="Edit Project Details"
                                >
                                    <Pencil size={18} />
                                </button>
                            </div>
                            
                            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight font-poppins-light-italic drop-shadow-lg leading-[0.9]">
                                {heroProject.title}
                            </h1>
                            
                            <p className="text-zinc-300 text-base md:text-xl md:leading-relaxed mb-8 font-light drop-shadow-md max-w-2xl">
                                {heroProject.logline || "No logline set. Click to enter the war room."}
                            </p>

                            <div className="flex flex-wrap items-center gap-4">
                                <button className="flex items-center gap-2 px-8 py-4 bg-white text-black font-bold rounded-xl hover:bg-zinc-200 transition-colors shadow-lg shadow-white/5">
                                    <Film size={20} />
                                    <span>Open Studio</span>
                                </button>
                                {heroProject.budgetLevel && (
                                    <div className="px-5 py-4 bg-black/60 border border-white/10 rounded-xl text-zinc-300 text-sm font-mono flex items-center gap-2 backdrop-blur-md">
                                        <DollarSign size={16} className="text-green-400" />
                                        {heroProject.budgetLevel.split(' ')[0]}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* --- 3. PROJECT GRID --- */}
            <div>
                {(showHero || gridProjects.length > 0) && (
                     <div className="flex items-center gap-4 mb-6 px-1">
                        <h3 className="text-zinc-500 text-xs font-bold uppercase tracking-widest">
                            {isSelectionMode ? 'Select Projects' : 'All Projects'}
                        </h3>
                        <div className="h-px bg-zinc-900 flex-grow" />
                        <span className="text-zinc-600 text-xs font-mono">
                            {gridProjects.length} Items
                        </span>
                     </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                    <AnimatePresence mode="popLayout">
                        {gridProjects.map((project) => (
                            <ProjectCard 
                                key={project.id}
                                project={project}
                                isSelected={selectedIds.has(project.id)}
                                isSelectionMode={isSelectionMode}
                                onToggleSelection={(e) => toggleSelection(project.id, e)}
                                onClick={() => isSelectionMode ? toggleSelection(project.id) : onSelectProject(project.id)}
                                onEdit={(e) => { e.stopPropagation(); openEditModal(project); }}
                                setHoveredProjectId={setHoveredProjectId}
                            />
                        ))}
                    </AnimatePresence>
                </div>
            </div>

            {/* --- 4. FLOATING BULK ACTION BAR --- */}
            <AnimatePresence>
                {selectedIds.size > 0 && (
                    <motion.div 
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        className="fixed bottom-6 left-0 right-0 flex justify-center z-40 px-4 pointer-events-none"
                    >
                        <div className="pointer-events-auto bg-zinc-900/90 border border-zinc-700 shadow-2xl backdrop-blur-xl rounded-2xl p-2 pl-6 flex items-center gap-6 max-w-lg w-full">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-500 text-white text-xs font-bold">
                                    {selectedIds.size}
                                </div>
                                <span className="text-zinc-200 text-sm font-medium">Selected</span>
                            </div>
                            <div className="h-8 w-px bg-zinc-700/50" />
                            <div className="flex items-center gap-2 ml-auto">
                                <button 
                                    onClick={() => setSelectedIds(new Set())}
                                    className="px-4 py-2 text-xs font-bold text-zinc-400 hover:text-white transition-colors"
                                >
                                    CANCEL
                                </button>
                                <button 
                                    onClick={initiateBulkDelete}
                                    className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20 rounded-lg text-xs font-bold transition-all flex items-center gap-2"
                                >
                                    <Trash2 size={14} />
                                    DELETE
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* --- EDIT MODAL (UPDATED LAYOUT) --- */}
            <AppModal
                isOpen={!!editingProject}
                onClose={() => setEditingProject(null)}
                title="Edit Project Details"
                size="xl" // Increased size for side-by-side layout
            >
                {editingProject && (
                    <form onSubmit={handleEditSave} className="p-6 md:p-8">
                        <div className="flex flex-col md:flex-row gap-8">
                            
                            {/* LEFT COL: INPUTS */}
                            <div className="flex-1 space-y-5">
                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Project Title</label>
                                    <input 
                                        type="text" 
                                        value={editingProject.title} 
                                        onChange={(e) => setEditingProject({...editingProject, title: e.target.value})}
                                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors text-lg font-bold"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Logline</label>
                                    <textarea 
                                        rows={4}
                                        value={editingProject.logline || ''} 
                                        onChange={(e) => setEditingProject({...editingProject, logline: e.target.value})}
                                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-zinc-300 focus:text-white focus:outline-none focus:border-purple-500 transition-colors resize-none leading-relaxed"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Genre</label>
                                        <div className="relative">
                                            <select 
                                                value={editingProject.genre || ''} 
                                                onChange={(e) => setEditingProject({...editingProject, genre: e.target.value})}
                                                className="w-full appearance-none bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                                            >
                                                {["Action", "Horror", "Drama", "Comedy", "Sci-Fi", "Thriller", "Documentary", "Romance"].map(g => (
                                                    <option key={g} value={g}>{g}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Budget Level</label>
                                        <div className="relative">
                                            <select 
                                                value={editingProject.budgetLevel || ''} 
                                                onChange={(e) => setEditingProject({...editingProject, budgetLevel: e.target.value})}
                                                className="w-full appearance-none bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                                            >
                                                <option value="">Select Tier...</option>
                                                {["Micro (<$500k)", "Indie ($500k - $5M)", "Mid-Tier ($5M - $50M)", "Studio (>$50M)"].map(b => (
                                                    <option key={b} value={b}>{b}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT COL: POSTER UPLOAD (Greenlight Style) */}
                            <div className="w-full md:w-72 flex flex-col gap-2">
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">Key Art</label>
                                <div className="relative w-full aspect-[2/3] group cursor-pointer">
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={handleFileSelect}
                                        className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-pointer"
                                    />
                                    
                                    <div className={`w-full h-full rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden relative flex flex-col items-center justify-center
                                        ${posterPreview ? 'border-transparent bg-black' : 'border-zinc-700 bg-zinc-800/50 group-hover:border-purple-500 group-hover:bg-zinc-800'}`}
                                    >
                                        {posterPreview ? (
                                            <>
                                                <img src={posterPreview} alt="Preview" className="w-full h-full object-cover opacity-100 group-hover:opacity-40 transition-opacity duration-300" />
                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                                                    <div className="bg-black/80 px-4 py-2 rounded-full border border-white/10 flex items-center gap-2">
                                                        <UploadCloud size={14} className="text-white" />
                                                        <span className="text-xs font-bold text-white">Change Art</span>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-center p-4">
                                                <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform text-zinc-500 group-hover:text-purple-400">
                                                    <UploadCloud size={24} />
                                                </div>
                                                <p className="text-zinc-300 font-bold text-xs mb-1">Upload Poster</p>
                                                <p className="text-zinc-500 text-[10px]">Click to browse</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <p className="text-center text-[10px] text-zinc-500">
                                    Recommended: 2:3 Portrait Ratio
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-8 mt-4 border-t border-zinc-800">
                            <button 
                                type="button"
                                onClick={() => setEditingProject(null)} 
                                className="px-6 py-2 rounded-lg text-sm font-bold text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                disabled={isSaving}
                                className="px-6 py-2 rounded-lg bg-white text-black text-sm font-bold hover:bg-zinc-200 transition-colors flex items-center gap-2"
                            >
                                {isSaving ? <span className="animate-pulse">Saving...</span> : <><Save size={16} /> Save Changes</>}
                            </button>
                        </div>
                    </form>
                )}
            </AppModal>

            {/* --- DELETE CONFIRMATION MODAL --- */}
            <AppModal
                isOpen={showDeleteModal}
                onClose={() => !isDeleting && setShowDeleteModal(false)}
                title="Confirm Deletion"
                size="sm"
            >
                <div className="text-center p-4">
                    <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/20 shadow-[0_0_30px_rgba(239,68,68,0.2)]">
                        {isDeleting ? (
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                        ) : (
                            <AlertTriangle className="w-8 h-8 text-red-500" />
                        )}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Delete {selectedIds.size} Project{selectedIds.size > 1 ? 's' : ''}?</h3>
                    <p className="text-zinc-400 text-sm mb-8 leading-relaxed max-w-xs mx-auto">
                        This action cannot be undone. All scripts, budgets, and AI data will be permanently erased.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <button 
                            onClick={() => setShowDeleteModal(false)} 
                            disabled={isDeleting}
                            className="px-4 py-3 rounded-lg bg-zinc-800 text-white font-bold hover:bg-zinc-700 transition-colors border border-zinc-700 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={confirmDelete} 
                            disabled={isDeleting}
                            className="px-4 py-3 rounded-lg bg-red-600 text-white font-bold hover:bg-red-500 transition-colors flex justify-center items-center gap-2 shadow-lg shadow-red-900/20 disabled:opacity-50"
                        >
                            {isDeleting ? 'Deleting...' : <><Trash2 size={16} /> Delete</>}
                        </button>
                    </div>
                </div>
            </AppModal>
        </div>
    );
};

// --- SUB-COMPONENT: PROJECT CARD ---
const ProjectCard = ({ project, isSelected, isSelectionMode, onToggleSelection, onClick, onEdit, setHoveredProjectId }) => {
    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onHoverStart={() => setHoveredProjectId(project.id)}
            onHoverEnd={() => setHoveredProjectId(null)}
            onClick={onClick}
            className={`
                group relative aspect-[2/3] rounded-xl cursor-pointer isolate overflow-hidden
                transition-all duration-300
                ${isSelected ? 'ring-2 ring-purple-500 scale-[0.98]' : 'hover:scale-[1.02]'}
            `}
        >
            <div className="absolute inset-0 bg-zinc-900">
                {project.poster_a ? (
                    <img 
                        src={project.poster_a} 
                        alt={project.title} 
                        className={`
                            w-full h-full object-cover transition-transform duration-700
                            ${isSelected ? 'scale-110 opacity-40 grayscale' : 'group-hover:scale-110 opacity-80 group-hover:opacity-100'}
                        `}
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-b from-zinc-800 to-zinc-950 flex flex-col items-center justify-center p-4 text-center">
                        <Film className="w-12 h-12 text-zinc-700 mb-4 group-hover:text-purple-500 transition-colors" />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-90" />
            </div>

            {/* SELECTION CHECKBOX */}
            <div 
                className={`
                    absolute top-3 left-3 z-30 transition-all duration-200
                    ${isSelectionMode || isSelected ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'}
                `}
                onClick={(e) => onToggleSelection(e)}
            >
                {isSelected ? (
                    <CheckCircle2 className="w-6 h-6 text-purple-500 fill-black" />
                ) : (
                    <Circle className="w-6 h-6 text-white/50 hover:text-white fill-black/20" />
                )}
            </div>

            {/* EDIT BUTTON */}
            {!isSelectionMode && (
                <div 
                    className="absolute top-3 right-3 z-30"
                    onClick={onEdit}
                >
                    <div className="p-2 rounded-full bg-black/50 text-white/50 hover:text-white hover:bg-black backdrop-blur-md shadow-lg transition-all border border-white/5">
                        <Pencil size={12} />
                    </div>
                </div>
            )}

            {/* BOTTOM INFO */}
            <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
                <h3 className="text-white font-bold text-base md:text-lg leading-tight mb-1 truncate drop-shadow-md">
                    {project.title || "Untitled"}
                </h3>
                <div className="flex items-center gap-2 text-[10px] text-zinc-400 font-medium uppercase tracking-wider">
                    <span className="truncate max-w-[80px]">{project.genre || 'TBD'}</span>
                    <span className="w-1 h-1 bg-zinc-600 rounded-full" />
                    <span>{project.releaseWindow || 'TBD'}</span>
                </div>
            </div>
            
            <div className="absolute inset-0 border border-white/10 rounded-xl pointer-events-none group-hover:border-white/20 transition-colors" />
        </motion.div>
    );
};

const EmptyState = ({ onCreateNew }) => (
    <div className="flex flex-col items-center justify-center h-[70vh] text-center px-4">
        <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-xl w-full bg-gradient-to-br from-zinc-900 to-black border border-white/10 p-12 rounded-3xl relative overflow-hidden"
        >
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/10 rounded-full blur-[100px] -mr-16 -mt-16 pointer-events-none" />
            
            <div className="relative z-10 flex flex-col items-center">
                <div className="w-20 h-20 bg-zinc-800/50 rounded-2xl flex items-center justify-center mb-6 border border-white/5 shadow-2xl rotate-3">
                    <Film className="w-8 h-8 text-zinc-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3 font-poppins">Studio Empty</h2>
                <p className="text-zinc-400 mb-8 max-w-xs mx-auto text-sm leading-relaxed">
                    Initialize your first project to unlock the AI marketing suite.
                </p>
                <button 
                    onClick={onCreateNew}
                    className="group px-8 py-3 bg-white text-black font-bold rounded-xl hover:scale-105 active:scale-95 transition-all shadow-lg shadow-white/5 flex items-center gap-2"
                >
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    Initialize Project
                </button>
            </div>
        </motion.div>
    </div>
);

export default ProjectDashboard;